import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';

export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  default: boolean;
}

export interface BillingLineItem {
  id: number;
  org: number;
  org_name: string;
  org_slug: string;
  fee_type: string;
  fee_type_display: string;
  project: number | null;
  payment_request: number | null;
  order: number | null;
  project_lot: string | null;
  description: string;
  base_amount: string;
  fee_amount: string;
  status: string;
  flagged_for_review: boolean;
  reviewed_by: number | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  reversible_until: string;
  reversed_at: string | null;
  can_reverse: boolean;
  created_at: string;
}

export interface BillingInvoice {
  id: number;
  org: number;
  org_name: string;
  stripe_invoice_id: string;
  stripe_invoice_url: string;
  stripe_invoice_pdf: string;
  period_start: string;
  period_end: string;
  total_amount: string;
  status: string;
  created_at: string;
}

export interface UpcomingCharges {
  total: number;
  item_count: number;
  by_type: Record<string, { count: number; total: number }>;
  next_bill_date: string;
}

export interface BillingStatus {
  billing_enabled: boolean;
  has_payment_method: boolean;
  /** ISO datetime the org's free trial ends, or null if no trial is tracked. */
  trial_ends_at?: string | null;
  /** True while trial_ends_at is in the future. */
  trial_active?: boolean;
  /** Org-admin-only ("Your trial has saved you $X in fees so far") — null
   * for any caller without admin.billing.* (can_manage_billing false). */
  trial_waived_total?: number | null;
  billing_lock_state?: 'good' | 'past_due_soft' | 'past_due_restricted' | 'hard_lock';
  billing_days_overdue?: number;
  oldest_open_invoice_date?: string | null;
  open_invoice_count?: number;
  open_total_amount?: number;
  billing_hard_locked?: boolean;
  billing_restricted?: boolean;
  can_manage_billing?: boolean;
  /** Effective payment-request processing fee rate as a percent, e.g. 2.5 = 2.5%. Falls back to the app's built-in default when absent (D8: settings-backed, constant fallback). */
  fee_percent_epo?: number;
  fee_percent_warranty?: number;
  /** Effective minimum processing fee in dollars, shared by EPO and warranty. */
  fee_minimum?: number;
  /** Effective project tech fee rate as a percent, e.g. 1 = 1%. Constant-backed
   * today, but sourced server-side through the same effective_fee_rate() as
   * epo/warranty so it follows automatically if that ever changes. */
  fee_percent_project?: number;
  /** Effective minimum project tech fee in dollars. */
  fee_minimum_project?: number;
}

/** Body of a 402 card_required response — a write against a non-draft
 * project with no card on file. Shared shape across every call site (see
 * billing.hardlock.CardRequiredForLiveProjectWrites / card_required_detail). */
export interface CardRequiredError {
  error: 'card_required';
  message: string;
  trial_ends_at?: string | null;
}

export interface SetupIntentResponse {
  client_secret: string;
  publishable_key: string;
}

@Injectable({ providedIn: 'root' })
export class BillingService {
  private http = inject(HttpClient);
  private base = `${environment.apiBaseUrl}/billing`;

  /**
   * Last-known hard-lock signal set either by `getBillingStatus()` or by the
   * auth interceptor on a 402 response. Anchors the hard-lock overlay so it
   * appears immediately on the first blocked request, before the next status
   * poll lands.
   */
  readonly hardLocked = signal(false);

  /** Number of days the oldest open invoice is overdue (best-effort cache). */
  readonly hardLockDaysOverdue = signal(0);

  /** True when the user has admin.billing.* (best-effort cache for the
   * overlay, and for the auth interceptor's card_required 402 handling —
   * see CardRequiredService / auth-interceptor.ts). */
  readonly canManageBilling = signal(false);

  async getCustomer(): Promise<any> {
    return firstValueFrom(this.http.get(`${this.base}/customer/`));
  }

  async createSetupIntent(): Promise<SetupIntentResponse> {
    return firstValueFrom(this.http.post<SetupIntentResponse>(`${this.base}/setup-intent/`, {}));
  }

  async getPaymentMethods(): Promise<PaymentMethod[]> {
    return firstValueFrom(this.http.get<PaymentMethod[]>(`${this.base}/payment-methods/`));
  }

  async setDefaultPaymentMethod(pmId: string): Promise<void> {
    await firstValueFrom(this.http.post(`${this.base}/payment-methods/${pmId}/default/`, {}));
  }

  async removePaymentMethod(pmId: string): Promise<void> {
    await firstValueFrom(this.http.delete(`${this.base}/payment-methods/${pmId}/`));
  }

  async getLineItems(params?: { status?: string; fee_type?: string }): Promise<BillingLineItem[]> {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.fee_type) query.set('fee_type', params.fee_type);
    const qs = query.toString();
    return firstValueFrom(this.http.get<BillingLineItem[]>(`${this.base}/line-items/${qs ? '?' + qs : ''}`));
  }

  async getInvoices(): Promise<BillingInvoice[]> {
    return firstValueFrom(this.http.get<BillingInvoice[]>(`${this.base}/invoices/`));
  }

  async getUpcoming(): Promise<UpcomingCharges> {
    return firstValueFrom(this.http.get<UpcomingCharges>(`${this.base}/upcoming/`));
  }

  async getFlaggedItems(params?: { status?: string }): Promise<BillingLineItem[]> {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    const qs = query.toString();
    return firstValueFrom(this.http.get<BillingLineItem[]>(`${this.base}/flagged/${qs ? '?' + qs : ''}`));
  }

  async approveFlaggedItem(id: number, reason = ''): Promise<BillingLineItem> {
    return firstValueFrom(this.http.post<BillingLineItem>(`${this.base}/flagged/${id}/approve/`, { reason }));
  }

  async rejectFlaggedItem(id: number, reason = ''): Promise<BillingLineItem> {
    return firstValueFrom(this.http.post<BillingLineItem>(`${this.base}/flagged/${id}/reject/`, { reason }));
  }

  private lastStatus: BillingStatus | null = null;
  private lastStatusAt = 0;

  async getBillingStatus(): Promise<BillingStatus> {
    const status = await firstValueFrom(
      this.http.get<BillingStatus>(`${this.base}/status/`)
    );
    this.hardLocked.set(!!status.billing_hard_locked);
    this.hardLockDaysOverdue.set(status.billing_days_overdue ?? 0);
    this.canManageBilling.set(!!status.can_manage_billing);
    this.lastStatus = status;
    this.lastStatusAt = Date.now();
    return status;
  }

  /**
   * Status for route guards: reuses a recent response so the billing-lock
   * check doesn't add a network round-trip to every dashboard navigation.
   * The interceptor's 402 handler covers a lock that lands mid-window.
   */
  async getBillingStatusCached(maxAgeMs = 30_000): Promise<BillingStatus> {
    if (this.lastStatus && Date.now() - this.lastStatusAt < maxAgeMs) {
      return this.lastStatus;
    }
    return this.getBillingStatus();
  }

  /** Called by the auth interceptor when any API returns 402 (hard lock). */
  noteHardLockFromError(daysOverdue?: number): void {
    this.hardLocked.set(true);
    if (typeof daysOverdue === 'number') {
      this.hardLockDaysOverdue.set(daysOverdue);
    }
  }

  /**
   * Forget everything known about the current org's lock state. Called on
   * profile switch and logout: the signals and status cache describe the
   * org that was active when they were set, and a stale `hardLocked` from a
   * suspended org would otherwise bounce a healthy org's dashboard
   * navigation straight back to /suspended.
   */
  resetLockState(): void {
    this.hardLocked.set(false);
    this.hardLockDaysOverdue.set(0);
    this.canManageBilling.set(false);
    this.lastStatus = null;
    this.lastStatusAt = 0;
  }

  async payNow(): Promise<BillingInvoice> {
    return firstValueFrom(this.http.post<BillingInvoice>(`${this.base}/pay-now/`, {}));
  }

  async retryInvoicePayment(invoiceId: number): Promise<BillingInvoice> {
    return firstValueFrom(
      this.http.post<BillingInvoice>(`${this.base}/invoices/${invoiceId}/retry_payment/`, {})
    );
  }
}
